import { useState, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Tabs, Tab,
  CircularProgress, Chip, useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import Tooltip from '@mui/material/Tooltip';
import { fmtC, fmtD, toLocalDateStr, INVOICE_STATUS_COLORS } from '../../lib/utils';
import { isInvoiceOverdue } from '../../lib/financialsEngine';
import { useInvoices } from '../../hooks/useInvoices';
import { useXeroStatus } from '../../hooks/useXero';
import FocusHide from '../shared/FocusHide';

const STATUSES = ['All', 'Unpaid', 'Sent', 'Partial', 'Paid', 'Overdue', 'Void'];

export default function InvoiceList({ selectedInvoiceId, onSelect, onNewInvoice }) {
  const { data: invoices = [], isLoading, error } = useInvoices();
  const { data: xeroStatus } = useXeroStatus();
  const xeroConnected = xeroStatus?.connected === true;
  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const hiddenOnMobile = isMobile ? { display: 'none' } : {};

  const filtered = useMemo(() => {
    let result = invoices;
    if (statusFilter === 'Overdue') {
      const today = toLocalDateStr(new Date());
      result = result.filter((inv) => isInvoiceOverdue(inv, today));
    } else if (statusFilter !== 'All') {
      result = result.filter((inv) => inv.status === statusFilter.toLowerCase());
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (inv) =>
          (inv.number || '').toLowerCase().includes(q) ||
          (inv.client || '').toLowerCase().includes(q)
      );
    }
    result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return result;
  }, [invoices, statusFilter, search]);

  if (error) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography color="error" variant="body2">Failed to load invoices: {error.message}</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Invoices</Typography>
        <FocusHide k="invoices.new">
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onNewInvoice}>New</Button>
        </FocusHide>
      </Box>

      <TextField
        size="small" fullWidth placeholder="Search by number or client..."
        value={search} onChange={(e) => setSearch(e.target.value)}
      />

      <Tabs
        value={STATUSES.indexOf(statusFilter)}
        onChange={(_, i) => setStatusFilter(STATUSES[i])}
        variant="scrollable" scrollButtons="auto"
        sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, py: 0.5, textTransform: 'none', fontSize: '0.75rem' } }}
      >
        {STATUSES.map((s) => <Tab key={s} label={s} />)}
      </Tabs>

      <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>No invoices found</Typography>
        ) : (
          <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { px: { xs: 0.75, md: 2 } } }}>
            <TableHead>
              <TableRow>
                <TableCell>Number</TableCell>
                <TableCell>Client</TableCell>
                <TableCell sx={hiddenOnMobile}>Type</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right" sx={hiddenOnMobile}>Paid</TableCell>
                <TableCell align="right" sx={hiddenOnMobile}>Balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={hiddenOnMobile}>Due Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((inv) => {
                const balance = (parseFloat(inv.amount) || 0) - (parseFloat(inv.paidAmount) || 0);
                const color = INVOICE_STATUS_COLORS[inv.status] || '#999';
                return (
                  <TableRow
                    key={inv.id} hover
                    onClick={() => onSelect(inv)}
                    selected={selectedInvoiceId === inv.id}
                    sx={{ cursor: 'pointer', borderLeft: selectedInvoiceId === inv.id ? '3px solid' : 'none', borderColor: 'primary.main' }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{inv.number}</TableCell>
                    <TableCell>{inv.client || '—'}</TableCell>
                    <TableCell sx={hiddenOnMobile}><Chip label={inv.invoiceType || 'full'} size="small" variant="outlined" /></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtC(inv.amount)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main', ...hiddenOnMobile }}>{fmtC(inv.paidAmount)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color, ...hiddenOnMobile }}>{fmtC(balance)}</TableCell>
                    <TableCell>
                      <Chip label={inv.status} size="small" sx={{ bgcolor: color + '20', color, borderColor: color + '40', fontWeight: 600 }} variant="outlined" />
                      {xeroConnected && inv.xeroInvoiceId && (
                        <Tooltip title="Synced to Xero" arrow>
                          <CloudDoneOutlinedIcon sx={{ fontSize: 16, ml: 0.5, color: 'success.main', verticalAlign: 'middle' }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', ...hiddenOnMobile }}>{fmtD(inv.dueDate) || '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Typography variant="caption" color="text.secondary" textAlign="center">
        {filtered.length} of {invoices.length} invoices
      </Typography>
    </Box>
  );
}
